/* eslint-disable quote-props */
import _ from 'lodash';
import { Geography, Projection, Industry, IndustryOption, WorldRegion } from './types/types';

export const titleCase = (str: string) => {
  if (str) {
    return str.trim().replace(/\b[a-z](?=[a-z]{2})/g, (sub: string, ...args: any[]) => sub.toUpperCase());
  } else {
    return str;
  }
};

const stateNameMap = new Map<string, string>(Object.entries({
  'alabama': 'AL',
  'alaska': 'AK',
  'arizona': 'AZ',
  'arkansas': 'AR',
  'california': 'CA',
  'colorado': 'CO',
  'connecticut': 'CT',
  'delaware': 'DE',
  'district of columbia': 'DC',
  'florida': 'FL',
  'georgia': 'GA',
  'hawaii': 'HI',
  'idaho': 'ID',
  'illinois': 'IL',
  'indiana': 'IN',
  'iowa': 'IA',
  'kansas': 'KS',
  'kentucky': 'KY',
  'louisiana': 'LA',
  'maine': 'ME',
  'maryland': 'MD',
  'massachusetts': 'MA',
  'michigan': 'MI',
  'minnesota': 'MN',
  'mississippi': 'MS',
  'missouri': 'MO',
  'montana': 'MT',
  'nebraska': 'NE',
  'nevada': 'NV',
  'new hampshire': 'NH',
  'new jersey': 'NJ',
  'new mexico': 'NM',
  'new york': 'NY',
  'north carolina': 'NC',
  'north dakota': 'ND',
  'ohio': 'OH',
  'oklahoma': 'OK',
  'oregon': 'OR',
  'pennsylvania': 'PA',
  'rhode island': 'RI',
  'south carolina': 'SC',
  'south dakota': 'SD',
  'tennessee': 'TN',
  'texas': 'TX',
  'utah': 'UT',
  'vermont': 'VT',
  'virginia': 'VA',
  'washington': 'WA',
  'west virginia': 'WV',
  'wisconsin': 'WI',
  'wyoming': 'WY'
}));

export const getDisplayedLocation = (geography?: Geography) => {
  if (!geography) return 'Region';
  const { state, country, worldRegion } = geography;
  if (country === 'united states') {
    return state ? `${state}, ${country}` : country;
  } else if (country !== '') {
    return country;
  } else {
    return worldRegion || 'Region';
  }
};

export const getShorthandDisplayedLocation = (geography: Geography) => {
  const { state, country, worldRegion } = geography;
  if (state) {
    return stateNameMap.get(state) || '';
  } else if (country) {
    return country;
  } else {
    return worldRegion;
  }
};

export const getYear5Revenue = (financialProjections: Projection[]) => {
  const currentYear = (new Date()).getFullYear();
  const year5Projection = (financialProjections || []).find((proj) => proj.year === currentYear + 4);
  return year5Projection?.revenue;
};

export const placeMatchingGeographyFirst = (investorGeographyList: Geography[], entGeography: Geography) => {
  let matchingGeography: Geography[] = []; const countryMatchingGeography: Geography[] = []; const worldRegionMatchingGeography: Geography[] = []; const nonMatchingGeography: Geography[] = [];
  let anyMatch: Geography[] | undefined;
  investorGeographyList.forEach((investorGeography) => {
    if (investorGeography.worldRegion === WorldRegion.Any) {
      anyMatch = [investorGeography];
    }
    if (_.isEqual(investorGeography.state, entGeography.state) && _.isEqual(investorGeography.country, entGeography.country) && _.isEqual(investorGeography.worldRegion, entGeography.worldRegion)) {
      matchingGeography.push(investorGeography);
    } else if (!investorGeography.state && _.isEqual(investorGeography.country, entGeography.country) && _.isEqual(investorGeography.worldRegion, entGeography.worldRegion)) {
      countryMatchingGeography.push(investorGeography);
    } else if (!investorGeography.state && !investorGeography.country && _.isEqual(investorGeography.worldRegion, entGeography.worldRegion)) {
      worldRegionMatchingGeography.push(investorGeography);
    } else {
      nonMatchingGeography.push(investorGeography);
    }
  });
  matchingGeography = matchingGeography.concat(countryMatchingGeography, worldRegionMatchingGeography);
  const allGeography = matchingGeography.concat(nonMatchingGeography);
  return { list: anyMatch || allGeography, match: (anyMatch !== undefined || matchingGeography.length > 0) };
};

export const placeMatchingIndustriesFirst = (investorIndustryList: Industry[], entIndustryList: Industry[]) => {
  const invList = investorIndustryList.map((i) => i.industry);
  const entList = entIndustryList.map((i) => i.industry);
  const intersection = new Set(_.intersection(invList, entList));
  const matchingInvestorItemsFirst = invList.sort((a, b) => {
    if (a === IndustryOption.Any) {
      return -1;
    }
    if (b === IndustryOption.Any) {
      return 1;
    }
    if (intersection.has(a)) {
      return -1;
    }
    if (intersection.has(b)) {
      return 1;
    }
    return 0;
  });
  const matchingEntItemsFirst = entList.sort((a, b) => {
    const indexOfA = matchingInvestorItemsFirst.indexOf(a);
    const indexOfB = matchingInvestorItemsFirst.indexOf(b);

    if (indexOfA >= 0 && indexOfB >= 0) {
      return indexOfA - indexOfB;
    }

    if (indexOfA >= 0) {
      return -1;
    }

    if (indexOfB >= 0) {
      return 1;
    }

    return 0;
  });
  return { investorIndustryList: matchingInvestorItemsFirst, entIndustryList: matchingEntItemsFirst };
};

export const ENT_MATCH_THRESHOLD = 80;
